import React, { type ErrorInfo, type PropsWithChildren, useCallback } from 'react';

import { ErrorBoundary as BaseErrorBoundary } from 'react-error-boundary';

import { useInteractionContext } from '@atlaskit/react-ufo/use-interaction-context';
import {
	addError,
	addErrorToAll,
	getActiveInteraction,
} from '@atlaskit/react-ufo/interaction-metrics';

import { useErrorAnalytics } from '../analytics/use-error-analytics';

type ErrorBoundaryProps = PropsWithChildren<{
	variantId?: string;
}>;

/**
 * Error boundary wrapper for the edition benefits modal
 * Catches React rendering errors and sends operational analytics events
 * Integrates with UFO (Atlaskit Reliability Framework) for error tracking
 */
export const ErrorBoundary = ({ children, variantId }: ErrorBoundaryProps): React.JSX.Element => {
	const handleOnError = useErrorAnalytics({ variantId });
	const ufoContext = useInteractionContext();

	const onError = useCallback(
		(error: Error, errorInfo: ErrorInfo) => {
			const interaction = getActiveInteraction();

			handleOnError(error, errorInfo);

			if (interaction && ufoContext) {
				addError(
					interaction?.id,
					error.name,
					ufoContext?.labelStack,
					'interaction error',
					error.message,
					error.stack,
				);
			} else {
				addErrorToAll(
					`ErrorBoundary:edition-benefits-modal${variantId ? `:${variantId}` : ''}`,
					ufoContext?.labelStack || null,
					'interaction error',
					error.message,
				);
			}
		},
		[handleOnError, variantId, ufoContext],
	);

	return (
		<BaseErrorBoundary onError={onError} fallback={<></>}>
			{children}
		</BaseErrorBoundary>
	);
};
